<template>
  <div class="result-view">
    <!-- Tab Buttons -->
    <div
      ref="tabContainer"
      :class="{
        'tab-buttons': dataStore.isSaveBTNclickedonce,
        'tab-buttons-disabled': !dataStore.isSaveBTNclickedonce,
        'has-scrollbar': isScrollbarVisible,
        'journal-tab-active': dataStore.activeTab === 'journal',
        'version-tab-active': dataStore.activeTab === 'version'
      }">
      <div
        class="tab-button"
        id="journal"
        @click="dataStore.activeTab = 'journal'"
        :class="{
          'bg-white': dataStore.activeTab === 'journal' && dataStore.journalErrorcount == 0,
          'bg-gray': dataStore.activeTab !== 'journal' && dataStore.journalErrorcount == 0,
          'error-bg-white': dataStore.activeTab === 'journal',
          'error-bg-gray': dataStore.activeTab !== 'journal'
        }">
        <img v-if="dataStore.journalErrorcount" src="@/assets/Journal.png" />
        <img v-else src="@/assets/journal_gray.png" />
        {{ t('result_view.journal') }}
      </div>
      <div
        class="tab-button"
        @click="dataStore.activeTab = 'log'"
        :class="{
          'error-bg-white': dataStore.activeTab === 'log',
          'error-bg-gray': dataStore.activeTab !== 'log',
          'bg-white': dataStore.activeTab === 'log' && dataStore.logErrorcount == 0,
          'bg-gray': dataStore.activeTab !== 'log' && dataStore.logErrorcount == 0
        }">
        <img v-if="dataStore.logErrorcount" src="@/assets/Log.png" />
        <img v-else src="@/assets/log_gray.png" />
        {{ t('result_view.log') }}
      </div>
      <div
        class="tab-button"
        @click="dataStore.activeTab = 'responsetimeanalysis'"
        :class="{'bg-blue': dataStore.activeTab === 'responsetimeanalysis', 'bg-gray': dataStore.activeTab !== 'responsetimeanalysis'}">
        <img v-if="dataStore.activeTab !== 'responsetimeanalysis'" src="@/assets/response_time_analysis.png" />
        <img v-else src="@/assets/response_time_analysis_blue.png" />
        {{ t('result_view.responsetimeanalysis') }}
      </div>
      <div
        class="tab-button"
        @click="dataStore.activeTab = 'bc'"
        :class="{'bg-blue': dataStore.activeTab === 'bc', 'bg-gray': dataStore.activeTab !== 'bc'}">
        <img v-if="dataStore.activeTab !== 'bc'" src="@/assets/Business.png" />
        <img v-else src="@/assets/business_blue.png" />
        {{ t('result_view.cases') }}
      </div>
      <div
        class="tab-button"
        @click="dataStore.activeTab = 'tax'"
        :class="{'bg-blue': dataStore.activeTab === 'tax', 'bg-gray': dataStore.activeTab !== 'tax'}">
        <img v-if="dataStore.activeTab !== 'tax'" src="@/assets/Tax.png" />
        <img v-else src="@/assets/tax_blue.png" />
        {{ t('result_view.tax') }}
      </div>
      <div
        class="tab-button"
        @click="dataStore.activeTab = 'version'"
        :class="{'bg-blue': dataStore.activeTab === 'version', 'bg-gray': dataStore.activeTab !== 'version'}">
        <img v-if="dataStore.activeTab !== 'version'" src="@/assets/Version_History.png" />
        <img v-else src="@/assets/version_blue.png" />
        {{ t('result_view.version') }}
      </div>
    </div>

    <!-- Tab Content -->
    <div
      class="tab-view"
      :class="{
        'edge-repair-tab-view-journal': dataStore.activeTab === 'journal',
        'edge-repair-tab-view-version': dataStore.activeTab === 'version',
        'tab-buttons-disabled-tab-view': !dataStore.isSaveBTNclickedonce && !dataStore.isLoading,
        'has-scrollbar': isScrollbarVisible
      }">
      <div class="start-screen" v-if="dataStore.activeTab === ''">
        <div class="start-screen-content">
          <div>
            <img src="@/assets/Upload_big.png" />
          </div>
          <div class="start-screen-text">{{ t('result_view.please_upload_file') }}</div>
        </div>
      </div>
      <JournalAnalysis v-if="dataStore.activeTab === 'journal'" @open-modal="openModal" />
      <LogAnalysis v-if="dataStore.activeTab === 'log'" @open-modal="openModal" />
      <ResponseTimeAnalysis v-if="dataStore.activeTab === 'responsetimeanalysis'" @open-modal="openModal" />
      <BusinessCases v-if="dataStore.activeTab === 'bc'" />
      <TaxGroups v-if="dataStore.activeTab === 'tax'" />
      <VersionHistory v-if="dataStore.activeTab === 'version'" />
    </div>

    <!-- Modal for Error Details -->
    <div v-if="isModalOpen" class="modal-overlay" @click.self="closeModal">
      <div class="modal-box-errors">
        <button v-if="!hideCopyButton" class="copyButton" @click="copyToClipboard">
          <i class="copy-icon"></i>
          {{ t('result_view.copy') }}
        </button>
        <pre id="content" class="content">{{ selectedContent }}</pre>
      </div>
    </div>

    <!-- Snackbar -->
    <div v-if="showSnackbar" class="snackbar">{{ t('result_view.copied') }}</div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useDataStore } from '@/stores/dataStore.js'
import JournalAnalysis from './resultview_components/JournalAnalysis.vue'
import LogAnalysis from './resultview_components/LogAnalysis.vue'
import ResponseTimeAnalysis from './resultview_components/ResponseTimeAnalysis.vue'
import BusinessCases from './resultview_components/BusinessCases.vue'
import TaxGroups from './resultview_components/TaxGroups.vue'
import VersionHistory from './resultview_components/VersionHistory.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const dataStore = useDataStore()

// Reactive state
const isScrollbarVisible = ref(false)
const isModalOpen = ref(false)
const selectedContent = ref(null)
const showSnackbar = ref(false)
const hideCopyButton = ref(false)
const tabContainer = ref(null)

// Methods
const checkScrollbar = () => {
  if (tabContainer.value) { isScrollbarVisible.value = tabContainer.value.scrollWidth > tabContainer.value.clientWidth }
}

const openModal = (entry) => {
  hideCopyButton.value = entry?.hideCopyButton === true
  if (entry?.Tra) { selectedContent.value = entry.Tra }
  else if (entry?.data?.Lines) { selectedContent.value = entry.data.Lines.map(line => line ?? '').join("\n") }
  isModalOpen.value = true
}

const closeModal = () => {
  isModalOpen.value = false
  selectedContent.value = null
  hideCopyButton.value = false
}

const copyToClipboard = () => {
  const content = document.getElementById('content')
  const range = document.createRange()
  const selection = window.getSelection()

  range.selectNodeContents(content)
  selection.removeAllRanges()
  selection.addRange(range)

  navigator.clipboard.writeText(selection.toString())
    .then(() => { showSnackbarMessage() })
    .catch(err => { console.error("Failed to copy text: ", err) })
  selection.removeAllRanges()
}

const showSnackbarMessage = () => {
  showSnackbar.value = true
  setTimeout(() => {
    showSnackbar.value = false
  }, 3000)
}

// Lifecycle hooks
onMounted(() => {
  checkScrollbar()
  window.addEventListener('resize', checkScrollbar)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkScrollbar)
})
</script>

<style scoped>
.tab-buttons {
  display: flex;
  background-color: var(--main-background-color);
  font-size: 14px;
  gap: 16px;
  overflow-x: auto;
  white-space: nowrap;
}

.tab-buttons-disabled {
  display: flex;
  background-color: var(--main-background-color);
  font-size: 14px;
  gap: 16px;
  pointer-events: none;
  white-space: nowrap;
}

.tab-button {
  min-width: 180px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  line-height: 17px;
  font-weight: 600;
  border-radius: 10px 10px 0 0;
  padding: 10px;
  flex-direction: row;
}

.tab-buttons::-webkit-scrollbar {
  height: 8px;
}

.tab-buttons::-webkit-scrollbar-track {
  background-color: var(--background-color-secondary);
  border-radius: 10px 10px 0 0;
}

.tab-buttons::-webkit-scrollbar-thumb {
  background-color: var(--scrollbar-thumb);
  border-radius: 10px;
}

.tab-buttons::-webkit-scrollbar-thumb:hover {
  background-color: var(--scrollbar-thumb-hover);
}

.tab-view {
  background-color: var(--background-color-secondary);
  border-radius: 10px;
  height: 100%;
  overflow-y: auto;
}

.tab-view.has-scrollbar {
  border-radius: 0 0 10px 10px;
}

.error-bg-gray {
  background-color: var(--error-bg-gray-background-color);
  color: var(--error-bg-gray-text-color);
}

.error-bg-white {
  background-color: var(--error-bg-white-background-color);
  color: var(--error-bg-white-text-color);
}

.bg-gray {
  background-color: var(--bg-gray-background-color);
  color: var(--bg-gray-text-color);
}

.bg-white {
  background-color: var(--bg-white-background-color);
  color: var(--bg-white-text-color);
}

.bg-blue {
  background-color: var(--bg-blue-background-color);
  color: var(--bg-blue-text-color);
}

.start-screen {
  display: flex;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
}

.start-screen-content {
  color: var(--start-screen-text-color);
  display: flex;
  flex-direction: column;
  font-size: 16px;
  font-weight: 500;
  align-items: center;
}

.journal-tab-active::-webkit-scrollbar-track {
  background-color: var(--background-color-secondary);
  border-radius: 0 10px 0 0;
}

.version-tab-active::-webkit-scrollbar-track {
  background-color: var(--background-color-secondary);
  border-radius: 10px 0 0 0;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 900;
}

.modal-box-errors {
  background-color: var(--box-background-color);
  border-radius: 8px;
  min-width: 1000px;
  min-height: 50px;
  padding: 35px;
  position: relative;
  flex-direction: column;
  max-width: 90%;
  max-height: 90%;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.9);
  overflow: auto;
}

@media (max-width: 1110px) {
  .modal-box-errors {
    min-width: 90%;
  }
}

/* Close Button in the error window */
.close {
  color: #aaa;
  float: right;
  font-size: 28px;
  font-weight: bold;
  cursor: pointer;
}

.close:hover,
.close:focus {
  color: black;
  text-decoration: none;
}

.content {
  margin: 0;
}

.copyButton {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: var(--copyToClipboardButton-background-color);
  color: var(--copyToClipboardButton-text-color);
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.copy-icon {
  width: 16px;
  height: 16px;
  background-image: url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white"><path d="M9 2h10a2 2 0 012 2v14a2 2 0 01-2 2h-2v2h-4v-2H9a2 2 0 01-2-2V4a2 2 0 012-2zm1 2v12h8V4H10zm-4 4h2v10h10v2H6a2 2 0 01-2-2V8h2z"></path></svg>');
  background-repeat: no-repeat;
  background-size: contain;
}

.snackbar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: var(--snackBar-background-color);
  color: var(--snackBar-text-color);
  padding: 12px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  opacity: 0;
  animation: fade-in-out 3s ease-in-out;
  z-index: 901;
}

@keyframes fade-in-out {
  0%, 100% { opacity: 0; }
  10%, 90% { opacity: 1; }
}

.result-view {
  display: flex;
  flex-direction: column;
  background-color: var(--main-background-color);
  height: 100%;
}

.edge-repair-tab-view-journal {
  border-radius: 0px 10px 10px 10px;
}

.edge-repair-tab-view-version {
  border-radius: 10px 10px 10px 10px;
}

@media (max-width: 1408px) and (min-width: 1407px),
       (max-width: 1025px) and (min-width: 1024px) {
  .edge-repair-tab-view-version {
    border-radius: 10px 8px 10px 10px;
  }
}

@media (max-width: 1406px) and (min-width: 1405px),
       (max-width: 1023px) and (min-width: 1022px) {
  .edge-repair-tab-view-version {
    border-radius: 10px 6px 10px 10px;
  }
}

@media (max-width: 1404px) and (min-width: 1403px),
       (max-width: 1021px) and (min-width: 1020px) {
  .edge-repair-tab-view-version {
    border-radius: 10px 4px 10px 10px;
  }
}

@media (max-width: 1402px) and (min-width: 1401px),
       (max-width: 1019px) and (min-width: 1018px) {
  .edge-repair-tab-view-version {
    border-radius: 10px 2px 10px 10px;
  }
}

@media (max-width: 1400px) and (min-width: 1201px),
       (max-width: 1017px) {
  .edge-repair-tab-view-version {
    border-radius: 10px 0px 10px 10px;
  }
}
</style>
